import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useContent, CONTACT_DEFAULTS } from "@/lib/content";
import { useAuth } from "@/context/AuthContext";
import { Phone, Mail, Clock, MapPin, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const c = useContent("contact", CONTACT_DEFAULTS);
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", form); setSent(true); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const details = [
    c.contact_phone && { Icon: Phone, label: "Phone", value: c.contact_phone, href: `tel:${c.contact_phone.replace(/[^\d+]/g, "")}`, id: "phone" },
    c.contact_email && { Icon: Mail, label: "Email", value: c.contact_email, href: `mailto:${c.contact_email}`, id: "email" },
    c.contact_hours && { Icon: Clock, label: "Hours", value: c.contact_hours, id: "hours" },
    c.contact_address && { Icon: MapPin, label: "Location", value: c.contact_address, id: "address" },
  ].filter(Boolean);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #e11d48 0, transparent 40%)" }} />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-400">{c.contact_eyebrow}</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="contact-heading">{c.contact_heading}</h1>
          <p className="mt-3 max-w-2xl text-slate-300">{c.contact_subtext}</p>
        </div>
      </section>
      <div className="mx-auto grid w-full max-w-6xl flex-1 gap-8 px-4 py-12 sm:px-6 lg:grid-cols-5">
        <div className="space-y-3 lg:col-span-2" data-testid="contact-details">
          {details.map((d) => (
            <div key={d.id} data-testid={`contact-${d.id}`} className="flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-rose-50 dark:bg-rose-950/40 text-rose-600"><d.Icon className="h-5 w-5" strokeWidth={2.5} /></div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{d.label}</p>
                {d.href ? <a href={d.href} className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 hover:text-rose-600 break-words">{d.value}</a>
                  : <p className="whitespace-pre-line font-display text-lg font-bold text-slate-900 dark:text-slate-100">{d.value}</p>}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 lg:col-span-3">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 md:text-lg">{c.contact_form_title}</h2>
          {sent ? (
            <div className="mt-6 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700" data-testid="contact-success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> <p>{c.contact_form_success}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Email</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Phone (optional)</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" className="mt-1.5" placeholder="+1 415 555 1234" /></div>
              <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Message</Label><Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="mt-1.5" placeholder="Tell us what you're looking for or what you need help with…" /></div>
              <Button type="submit" disabled={busy} data-testid="contact-submit" className="rounded-md bg-rose-600 py-6 font-bold text-white hover:bg-rose-700 sm:col-span-2">
                {busy ? "Sending…" : <><Send className="mr-2 h-4 w-4" /> Send message</>}
              </Button>
            </form>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
